<script setup lang="ts">
  import EyeIcon from '@/shared/assets/icons/EyeActionIcon.svg?component';
import filterIcon from '@/shared/assets/icons/Filter.svg';
import orderIcon from '@/shared/assets/icons/Order.svg';
import orderWhite from '@/shared/assets/icons/OrderWhite.svg';
import { MATRIX_MODEL_ORDER_OPTIONS } from '@/shared/config';
import { formatInstantToPtBR, type SortOrder } from '@/shared/lib';
import { useFormBuilderToast } from '../../composables/useFormBuilderToast';
import { Checkbox, Chip, ColoredButton, Tooltip } from '@/shared/ui';
import UiModalHeader from '@/shared/ui/modals/UiModalHeader.vue';
import type { SearchComboboxOption } from '@/shared/ui/search-combobox';
import SearchBar from '@/shared/ui/search/SearchBar.vue';
import { onClickOutside, useDebounceFn } from '@vueuse/core';
import { Loader2, SquarePlus } from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { VueFinalModal } from 'vue-final-modal';
import { matrixCategoryApi, matrixModelApi, type MatrixModelSelectionItem } from '../../api/form-builder.api';
import { useFormBuilderStore } from '../../model/form-builder.store';
import { pageOrientation, type PageOrientation } from '@record-fill';
import ModelsFilterPanel, { type ModelsFilterValue } from './ModelsFilterPanel.vue';

  const MAX_SELECTION = 5;

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'adoptOrientation', orientation: PageOrientation): void;
  }>();

  const { showErrorToast, showSuccessToast } = useFormBuilderToast();
  const formBuilderStore = useFormBuilderStore();
  const router = useRouter();

  const models = ref<MatrixModelSelectionItem[]>([]);
  const isLoading = ref(true);
  const searchTerm = ref('');
  const selectedIds = ref<number[]>([]);

  const DEFAULT_SORT: 'createdAt' | 'title' = 'createdAt';
  const DEFAULT_ORDER: SortOrder = 'desc';

  const sort = ref<'createdAt' | 'title'>(DEFAULT_SORT);
  const order = ref<SortOrder>(DEFAULT_ORDER);
  const createdAtFilter = ref<string | null>(null);
  const selectedCategoryIds = ref<number[]>([]);

  const isFilterOpen = ref(false);
  const categoryOptions = ref<SearchComboboxOption[]>([]);

  const isOrderDropdownOpen = ref(false);
  const orderDropdownRef = ref<HTMLElement | null>(null);

  const currentQueryId = ref(0);

  const hasActiveFilters = computed(
    () => createdAtFilter.value !== null || selectedCategoryIds.value.length > 0
  );
  const isSearchOrFilterActive = computed(
    () => searchTerm.value.trim().length > 0 || hasActiveFilters.value
  );

  const selectedOrderOption = ref<(typeof MATRIX_MODEL_ORDER_OPTIONS)[number] | null>(null);

  async function fetchModels(): Promise<void> {
    isLoading.value = true;
    const queryId = ++currentQueryId.value;
    try {
      const result = await matrixModelApi.list({
        search: searchTerm.value.trim() || undefined,
        sort: sort.value,
        order: order.value,
        createdAt: createdAtFilter.value ?? undefined,
        categoryIds: selectedCategoryIds.value.length ? selectedCategoryIds.value : undefined,
      });
      if (queryId === currentQueryId.value) {
        models.value = result;
        selectedIds.value = [];
      }
    } catch (error) {
      if (queryId === currentQueryId.value) {
        console.error('Error fetching matrix models:', error);
        showErrorToast({
          title: 'Erro ao carregar modelos',
          type: 'error',
          hideIcon: true,
        });
      }
    } finally {
      if (queryId === currentQueryId.value) {
        isLoading.value = false;
      }
    }
  }

  const debouncedFetch = useDebounceFn(fetchModels, 300);

  const hasSelection = computed(() => selectedIds.value.length > 0);

  const isSelectionLimitReached = computed(() => selectedIds.value.length >= MAX_SELECTION);

  function isSelected(id: number): boolean {
    return selectedIds.value.includes(id);
  }

  function toggleRow(id: number): void {
    if (selectedIds.value.includes(id)) {
      selectedIds.value = selectedIds.value.filter((selectedId) => selectedId !== id);
      return;
    }
    if (isSelectionLimitReached.value) return;
    selectedIds.value = [...selectedIds.value, id];
  }

  function handleSearch(value: string): void {
    searchTerm.value = value;
    debouncedFetch();
  }

  function appendModel(model: MatrixModelSelectionItem): void {
    formBuilderStore.appendModelStructure(model.structure?.elements ?? []);
  }

  // Only a matrix that starts from models takes their page; added to work already on the canvas,
  // a model must not turn the sheet the author is laying out. Runs before the append, which would
  // make the canvas look started.
  function adoptOrientationWhenStartingFrom(model: MatrixModelSelectionItem): void {
    const startsFromModels = formBuilderStore.elements.every(
      (element) => element.type === 'HEADER'
    );
    if (startsFromModels) {
      emit('adoptOrientation', pageOrientation(model.structure));
    }
  }

  function handleAddSelected(): void {
    if (!hasSelection.value) return;
    const selectedModels = selectedIds.value
      .map((id) => models.value.find((model) => model.id === id))
      .filter((model): model is MatrixModelSelectionItem => model !== undefined);
    if (selectedModels.length === 0) return;
    adoptOrientationWhenStartingFrom(selectedModels[0]);
    for (const model of selectedModels) {
      appendModel(model);
    }
    showSuccessToast({
      title:
        selectedModels.length > 1 ? 'Modelos adicionados à Matriz' : 'Modelo adicionado à Matriz',
      type: 'success',
      hideIcon: true,
    });
    emit('close');
  }

  function toggleOrderDropdown(): void {
    isOrderDropdownOpen.value = !isOrderDropdownOpen.value;
  }

  function handleSortOption(sortField: string, orderFlow: SortOrder): void {
    const isSameOption =
      selectedOrderOption.value?.sort === sortField &&
      selectedOrderOption.value?.order === orderFlow;
    if (isSameOption) {
      selectedOrderOption.value = null;
      sort.value = DEFAULT_SORT;
      order.value = DEFAULT_ORDER;
    } else {
      sort.value = sortField as 'createdAt' | 'title';
      order.value = orderFlow;
      selectedOrderOption.value =
        MATRIX_MODEL_ORDER_OPTIONS.find(
          (option) => option.sort === sortField && option.order === orderFlow
        ) ?? null;
    }
    isOrderDropdownOpen.value = false;
    fetchModels();
  }

  async function loadCategoryOptions(): Promise<SearchComboboxOption[]> {
    try {
      const categories = await matrixCategoryApi.list();
      return categories.map((c) => ({ value: String(c.id), label: c.name }));
    } catch (error) {
      console.error('Error loading matrix categories:', error);
      showErrorToast({
        title: 'Erro ao carregar categorias',
        type: 'error',
        hideIcon: true,
      });
      return [];
    }
  }

  function toggleFilter(): void {
    isFilterOpen.value = !isFilterOpen.value;
  }

  function handleApplyFilters(value: ModelsFilterValue): void {
    createdAtFilter.value = value.createdAt;
    selectedCategoryIds.value = value.categoryIds;
    isFilterOpen.value = false;
    fetchModels();
  }

  function handleClearFilters(): void {
    createdAtFilter.value = null;
    selectedCategoryIds.value = [];
    isFilterOpen.value = false;
    fetchModels();
  }

  function handlePreview(model: MatrixModelSelectionItem): void {
    const { href } = router.resolve({
      name: 'GestaoRegistrosModeloVisualizar',
      params: { id: model.id },
    });
    window.open(href, '_blank', 'noopener');
  }

  function handleAddOne(model: MatrixModelSelectionItem): void {
    adoptOrientationWhenStartingFrom(model);
    appendModel(model);
    showSuccessToast({
      title: 'Modelo adicionado à Matriz',
      type: 'success',
      hideIcon: true,
    });
    emit('close');
  }

  onClickOutside(orderDropdownRef, () => {
    isOrderDropdownOpen.value = false;
  });

  onMounted(async () => {
    void fetchModels();
    categoryOptions.value = await loadCategoryOptions();
  });
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col w-[1296px] max-w-[calc(100vw-2rem)] max-h-[85vh] bg-white-01 rounded-2xl shadow-xl"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <div class="px-8 pt-6 mb-4">
      <UiModalHeader
        title="Modelos"
        title-size="text-xl"
        text-color="gray-07"
        @close="emit('close')"
      />
    </div>

    <div class="px-8 mb-6">
      <SearchBar
        :initial-search-term="searchTerm"
        @search="handleSearch"
      />
    </div>

    <div class="flex-1 overflow-y-auto px-8 pb-6">
      <div class="border border-[var(--color-gray-02)] rounded-2xl">
        <div class="px-6 py-4 flex items-center justify-end gap-4">
          <div class="flex items-center gap-3">
            <ColoredButton
              data-testid="add-to-matrix-btn"
              aria-label="Adicionar à Matriz"
              text="Adicionar à Matriz"
              :icon="SquarePlus"
              icon-alt="Adicionar à Matriz"
              :disabled="!hasSelection"
              @click="handleAddSelected"
            />

            <div
              ref="orderDropdownRef"
              class="relative"
            >
              <ColoredButton
                text="Ordenar por"
                :icon="selectedOrderOption ? orderWhite : orderIcon"
                icon-alt="Ordenar"
                :outlined="!selectedOrderOption"
                @click="toggleOrderDropdown"
              />
              <div
                v-if="isOrderDropdownOpen"
                class="absolute right-0 top-full mt-2 w-72 p-2 bg-white-01 rounded-2xl shadow-2xl z-[1001]"
              >
                <button
                  v-for="option in MATRIX_MODEL_ORDER_OPTIONS"
                  :key="option.label"
                  type="button"
                  class="block w-full text-left px-4 py-2 text-base font-medium text-[var(--color-gray-07)] rounded-lg cursor-pointer"
                  :class="{
                    'bg-[var(--color-gray-02)]':
                      selectedOrderOption?.sort === option.sort &&
                      selectedOrderOption?.order === option.order,
                  }"
                  @click="handleSortOption(option.sort, option.order)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
            <ColoredButton
              text="Filtrar"
              :icon="filterIcon"
              icon-alt="Filtrar"
              :outlined="!hasActiveFilters && !isFilterOpen"
              @click="toggleFilter"
            />
          </div>
        </div>

        <div
          class="grid transition-[grid-template-rows] duration-300 ease-in-out"
          :class="isFilterOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
        >
          <div class="overflow-hidden">
            <div :class="{ 'border-b-2 border-[var(--theme-secondary-ink)]': isFilterOpen }">
              <ModelsFilterPanel
                :category-options="categoryOptions"
                :initial-created-at="createdAtFilter"
                :initial-category-ids="selectedCategoryIds"
                @apply="handleApplyFilters"
                @clear="handleClearFilters"
              />
            </div>
          </div>
        </div>

        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-gray-07 border-t border-[var(--color-gray-02)]">
              <th class="px-6 py-3 font-semibold w-1/3">
                Título
              </th>
              <th class="px-6 py-3 font-semibold">
                Categorias
              </th>
              <th class="px-6 py-3 font-semibold">
                Data de criação
              </th>
              <th class="px-6 py-3 font-semibold w-32">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoading">
              <td
                colspan="4"
                class="py-10 text-center text-[var(--color-gray-05)]"
                data-testid="models-loading"
              >
                <Loader2 class="w-6 h-6 animate-spin inline-block text-[var(--theme-secondary-ink)]" />
              </td>
            </tr>
            <tr v-else-if="models.length === 0">
              <td
                colspan="4"
                class="py-10 text-center text-[var(--color-gray-05)]"
              >
                {{
                  isSearchOrFilterActive ? 'Nenhum modelo encontrado' : 'Nenhum modelo cadastrado'
                }}
              </td>
            </tr>
            <tr
              v-for="model in models"
              v-else
              :key="model.id"
              class="border-t border-[var(--color-gray-02)]"
              :data-testid="`model-row-${model.id}`"
            >
              <td class="px-6 py-3">
                <label class="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    :id="`model-${model.id}`"
                    :checked="isSelected(model.id)"
                    :disabled="!isSelected(model.id) && isSelectionLimitReached"
                    @toggle="toggleRow(model.id)"
                  />
                  <span class="text-[var(--color-gray-07)]">{{ model.title }}</span>
                </label>
              </td>
              <td class="px-6 py-3">
                <div class="flex items-center gap-2 flex-wrap">
                  <Chip
                    v-for="category in model.categories"
                    :key="category.id"
                    :label="category.name"
                    :removable="false"
                  />
                </div>
              </td>
              <td class="px-6 py-3 text-[var(--color-gray-07)]">
                {{ formatInstantToPtBR(model.createdAt, { withTime: false }) }}
              </td>
              <td class="px-6 py-3">
                <div class="flex items-center gap-2">
                  <span :data-testid="`preview-btn-${model.id}`">
                    <Tooltip
                      content="Ver Modelo"
                      placement="top"
                    >
                      <button
                        type="button"
                        :aria-label="`Ver Modelo ${model.title}`"
                        class="inline-flex items-center justify-center w-9 h-9 rounded-md text-[var(--color-gray-03)] cursor-pointer shrink-0"
                        @click="handlePreview(model)"
                      >
                        <EyeIcon
                          viewBox="0 0 24 24"
                          class="size-6 shrink-0"
                        />
                      </button>
                    </Tooltip>
                  </span>
                  <Tooltip
                    content="Adicionar à Matriz"
                    placement="top"
                  >
                    <button
                      type="button"
                      :data-testid="`add-btn-${model.id}`"
                      :aria-label="`Adicionar ${model.title} à matriz`"
                      class="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[var(--theme-secondary)] text-white cursor-pointer"
                      @click="handleAddOne(model)"
                    >
                      <SquarePlus
                        :size="18"
                        color="white"
                      />
                    </button>
                  </Tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </VueFinalModal>
</template>
